import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Avatar,
  Typography,
  Grid,
  IconButton,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, ExpandMore as ExpandMoreIcon, Lock as LockIcon, Shield as ShieldIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api, { formatApiErrorMessage } from '../../services/api';

export function MfaCodeBoxes({
  value,
  onChange,
  onComplete,
  autoFocus = false,
  disabled = false,
}: {
  value: string;
  onChange: (code: string) => void;
  onComplete?: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const completedRef = useRef('');
  const digits = Array.from({ length: 6 }, (_, i) => (value.replace(/\D/g, '').charAt(i) || ''));

  const emit = (next: string) => {
    const code = next.replace(/\D/g, '').slice(0, 6);
    onChange(code);
    if (code.length === 6 && completedRef.current !== code) {
      completedRef.current = code;
      onComplete?.(code);
    }
    if (code.length < 6) {
      completedRef.current = '';
    }
  };

  return (
    <Box
      sx={{ display: 'flex', gap: 1, justifyContent: 'center', my: 1 }}
      role="group"
      aria-label="Authenticator code"
    >
      {digits.map((digit, i) => (
        <TextField
          key={i}
          value={digit}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          inputRef={(el) => {
            inputsRef.current[i] = el;
          }}
          inputProps={{
            maxLength: 1,
            inputMode: 'numeric',
            autoComplete: i === 0 ? 'one-time-code' : 'off',
            'aria-label': `Digit ${i + 1}`,
            style: { textAlign: 'center', fontSize: '1.25rem', fontFamily: 'monospace', padding: '10px 0' },
          }}
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, '');
            if (raw.length > 1) {
              emit(raw);
              inputsRef.current[Math.min(5, raw.length)]?.focus();
              return;
            }
            const next = digits.slice();
            next[i] = raw;
            emit(next.join(''));
            if (raw && i < 5) {
              inputsRef.current[i + 1]?.focus();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !digits[i] && i > 0) {
              const next = digits.slice();
              next[i - 1] = '';
              emit(next.join(''));
              inputsRef.current[i - 1]?.focus();
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
            if (pasted) {
              e.preventDefault();
              emit(pasted);
              inputsRef.current[Math.min(5, pasted.length)]?.focus();
            }
          }}
          sx={{ width: 44, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
        />
      ))}
    </Box>
  );
}

interface UserProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({ open, onClose }) => {
  const { user, updateUser, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);
  const [mfaPassword, setMfaPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaOtpauth, setMfaOtpauth] = useState('');
  const [mfaQrDataUrl, setMfaQrDataUrl] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState<string[]>([]);
  const [mfaEnabled, setMfaEnabled] = useState(Boolean(user?.mfaEnabled));
  const enablingMfaRef = useRef(false);

  useEffect(() => {
    setMfaEnabled(Boolean(user?.mfaEnabled));
  }, [user?.mfaEnabled]);

  useEffect(() => {
    if (!open || !user?.mfaSetupPending || mfaEnabled || mfaSecret) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/user/mfa/setup', {});
        if (!cancelled) {
          setMfaSecret(data.secret);
          setMfaOtpauth(data.otpauthUrl);
          setMfaQrDataUrl(data.qrDataUrl || '');
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(formatApiErrorMessage((err as { response?: { data?: unknown } })?.response?.data, 'Could not continue setup'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user?.mfaSetupPending, mfaEnabled, mfaSecret]);

  const handleEdit = () => {
    setIsEditing(true);
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || ''
    });
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      // Validate form data
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setError('First name and last name are required');
        return;
      }

      // Call API to update user profile (username and email are immutable here).
      // The backend compares the submitted email against the current one, so we
      // must echo the existing email back (omitting it triggers a server error).
      const response = await api.put('/user/profile', {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: user?.email,
      });

      const updatedUser = response.data;
      
      // Update the user context
      if (updateUser) {
        updateUser(updatedUser);
      }

      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    try {
      setError(null);
      setSuccess(null);

      // Validate password fields
      if (!passwordData.currentPassword.trim()) {
        setError('Current password is required');
        return;
      }
      if (!passwordData.newPassword.trim()) {
        setError('New password is required');
        return;
      }
      if (!passwordData.confirmPassword.trim()) {
        setError('Password confirmation is required');
        return;
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError('New passwords do not match');
        return;
      }
      // Validate password strength (same as backend policy)
      if (passwordData.newPassword.length < 8) {
        setError('Password must be at least 8 characters long');
        return;
      }
      if (!/[A-Z]/.test(passwordData.newPassword)) {
        setError('Password must contain at least one uppercase letter');
        return;
      }
      if (!/[a-z]/.test(passwordData.newPassword)) {
        setError('Password must contain at least one lowercase letter');
        return;
      }
      if (!/[0-9]/.test(passwordData.newPassword)) {
        setError('Password must contain at least one digit');
        return;
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword)) {
        setError('Password must contain at least one special character');
        return;
      }

      // Call API to change password
      const response = await api.post('/user/change-password', passwordData);

      setSuccess('Password changed successfully! You will be logged out and redirected to login page.');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Close the profile dialog
      onClose();

      // Redirect to login page after a short delay
      setTimeout(() => {
        // Clear auth context and redirect to login
        logout();
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    }
  };

  const enableMfaWithCode = async (code: string) => {
    const trimmed = code.replace(/\D/g, '');
    if (trimmed.length !== 6 || enablingMfaRef.current) {
      return;
    }
    enablingMfaRef.current = true;
    try {
      setError(null);
      const { data } = await api.post('/user/mfa/enable', { code: trimmed });
      setMfaEnabled(true);
      setMfaBackupCodes(data.backupCodes || []);
      setMfaSecret('');
      setMfaOtpauth('');
      setMfaQrDataUrl('');
      setMfaCode('');
      if (user && updateUser) {
        updateUser({ ...user, mfaEnabled: true, mfaSetupPending: false });
      }
    } catch (err: unknown) {
      setMfaCode('');
      setError(formatApiErrorMessage((err as { response?: { data?: unknown } })?.response?.data, 'Invalid authenticator code. Delete the old Narralyze entry, scan the QR again, and use the new code.'));
    } finally {
      enablingMfaRef.current = false;
    }
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    if (user?.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Avatar loading logic
  useEffect(() => {
    if (!user) return;

    const fetchAvatar = async () => {
      try {
        // Only fetch avatar if user.avatarUrl is a relative path
        if (user.avatarUrl && user.avatarUrl.startsWith('/')) {
          const response = await api.get('/user/avatar', {
            responseType: 'blob',
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
          const dataUrl = URL.createObjectURL(blob);
          setAvatarDataUrl(dataUrl);
        } else if (user.avatarUrl) {
          // If it's a full URL (like Google profile), use it directly
          setAvatarDataUrl(null);
        }
      } catch (error) {
        console.error('Error fetching avatar:', error);
        setAvatarDataUrl(null);
      }
    };

    fetchAvatar();

    return () => {
      if (avatarDataUrl) {
        URL.revokeObjectURL(avatarDataUrl);
      }
    };
  }, [user?.id]); // Only depend on user ID

  const getAvatarUrl = () => {
    // If user has a full URL (like Google profile), use it directly
    if (user?.avatarUrl && !user.avatarUrl.startsWith('/')) {
      return user.avatarUrl;
    }
    // Otherwise use the blob URL if we have one
    return avatarDataUrl || undefined;
  };

  return (
    <Dialog 
      open={open} 
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">User Profile</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            src={getAvatarUrl()}
            sx={{ 
              width: 80, 
              height: 80, 
              fontSize: '2rem',
              mr: 2
            }}
          >
            {getUserInitials()}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}`
                : user?.username || 'User'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email || ''}
            </Typography>
            {user?.admin && (
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                Administrator
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={isEditing ? formData.firstName : (user?.firstName || '')}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              disabled={!isEditing}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={isEditing ? formData.lastName : (user?.lastName || '')}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              disabled={!isEditing}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              value={user?.email || ''}
              disabled={true}
              sx={{ mb: 2 }}
              helperText="Email changes require verification. Please use account settings."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Username"
              value={user?.username || ''}
              disabled={true}
              sx={{ mb: 2 }}
              helperText="Username cannot be changed"
            />
          </Grid>
        </Grid>

        {!isEditing && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Account expiration:{' '}
              {user?.accountNeverExpires
                ? 'Never'
                : user?.expirationDate
                  ? new Date(user.expirationDate).toLocaleDateString()
                  : 'N/A'}
            </Typography>
            {user?.roles && user.roles.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Roles: {user.roles.join(', ')}
              </Typography>
            )}
          </Box>
        )}

        {/* Password Change Section - Only for LOCAL users */}
        {user?.provider === 'LOCAL' && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon sx={{ mr: 1 }} />
                <Typography>Change Password</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Current Password"
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="New Password"
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    sx={{ mb: 2 }}
                    helperText="Password must be at least 8 characters with uppercase, lowercase, digit, and special character"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirm New Password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleChangePassword}
                    startIcon={<LockIcon />}
                  >
                    Change Password
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {user?.provider === 'LOCAL' && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ShieldIcon sx={{ mr: 1 }} />
                <Typography>Two-step verification</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Optional. After your password, sign-in asks for a 6-digit code from any authenticator app
                (Google Authenticator, 1Password, Authy, and others).
              </Typography>
              {mfaEnabled && mfaBackupCodes.length === 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>Two-step verification is on. Sign-in will ask for a code after your password.</Alert>
              )}
              {!mfaEnabled && (user?.mfaSetupPending || mfaSecret) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Two-step verification is not on yet. If your app already has a Narralyze account, delete it
                  and scan the QR below, then enter the current 6-digit code and click Turn on.
                </Alert>
              )}
              {mfaBackupCodes.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Save these backup codes now. Each can be used once if you lose your authenticator.
                  <Box component="ul" sx={{ pl: 2, mb: 1, fontFamily: 'monospace' }}>
                    {mfaBackupCodes.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={() => {
                      const lines = [
                        'Narralyze backup codes',
                        user?.username ? `Account: ${user.username}` : null,
                        `Saved: ${new Date().toISOString().slice(0, 10)}`,
                        '',
                        'Each code can be used once if you lose your authenticator.',
                        '',
                        ...mfaBackupCodes,
                      ].filter((line): line is string => line !== null);
                      const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'narralyze-backup-codes.txt';
                      link.click();
                      URL.revokeObjectURL(url);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Save as text file
                  </Button>
                </Alert>
              )}
              {mfaSecret && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Scan this QR code with your authenticator app (skip if you already added Narralyze), then enter the current 6-digit code below and click Turn on.
                  {mfaQrDataUrl && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <Box
                        component="img"
                        src={mfaQrDataUrl}
                        alt="Authenticator QR code"
                        sx={{
                          width: 176,
                          height: 176,
                          p: 1.5,
                          bgcolor: '#fff',
                          borderRadius: 1
                        }}
                      />
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block">
                    Manual key
                  </Typography>
                  <Box sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}>{mfaSecret}</Box>
                  {mfaOtpauth && (
                    <Button href={mfaOtpauth} size="small" sx={{ mt: 1, textTransform: 'none' }}>
                      Open setup link
                    </Button>
                  )}
                </Alert>
              )}
              <TextField
                fullWidth
                type="password"
                label="Account password"
                value={mfaPassword}
                onChange={(e) => setMfaPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              {(mfaSecret || mfaEnabled) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Authenticator code
                  </Typography>
                  <MfaCodeBoxes
                    value={mfaCode.replace(/\D/g, '').slice(0, 6)}
                    onChange={setMfaCode}
                    autoFocus={Boolean(mfaSecret)}
                    onComplete={(code) => {
                      if (!mfaEnabled && mfaSecret) {
                        void enableMfaWithCode(code);
                      }
                    }}
                  />
                  {mfaEnabled && (
                    <TextField
                      fullWidth
                      size="small"
                      label="Or backup code"
                      value={mfaCode.includes('-') || /[A-Za-z]/.test(mfaCode) ? mfaCode : ''}
                      onChange={(e) => setMfaCode(e.target.value)}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              )}
              {!mfaEnabled && !mfaSecret && (
                <Button
                  variant="contained"
                  onClick={async () => {
                    try {
                      setError(null);
                      const { data } = await api.post('/user/mfa/setup', { currentPassword: mfaPassword });
                      setMfaSecret(data.secret);
                      setMfaOtpauth(data.otpauthUrl);
                      setMfaQrDataUrl(data.qrDataUrl || '');
                    } catch (err: unknown) {
                      setError(formatApiErrorMessage((err as { response?: { data?: unknown } })?.response?.data, 'Could not start setup'));
                    }
                  }}
                >
                  Start setup
                </Button>
              )}
              {!mfaEnabled && mfaSecret && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    onClick={() => void enableMfaWithCode(mfaCode)}
                  >
                    Turn on
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={async () => {
                      try {
                        setError(null);
                        const { data } = await api.post('/user/mfa/setup', { currentPassword: mfaPassword, reset: 'true' });
                        setMfaSecret(data.secret);
                        setMfaOtpauth(data.otpauthUrl);
                        setMfaQrDataUrl(data.qrDataUrl || '');
                        setMfaCode('');
                      } catch (err: unknown) {
                        setError(formatApiErrorMessage((err as { response?: { data?: unknown } })?.response?.data, 'Enter your current password to create a new QR code'));
                      }
                    }}
                  >
                    New QR code
                  </Button>
                </Box>
              )}
              {mfaEnabled && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={async () => {
                    try {
                      setError(null);
                      await api.post('/user/mfa/disable', { currentPassword: mfaPassword, code: mfaCode });
                      setMfaEnabled(false);
                      setMfaBackupCodes([]);
                      setMfaCode('');
                      setMfaPassword('');
                      if (user && updateUser) {
                        updateUser({ ...user, mfaEnabled: false });
                      }
                    } catch (err: unknown) {
                      setError(formatApiErrorMessage((err as { response?: { data?: unknown } })?.response?.data, 'Could not turn off'));
                    }
                  }}
                >
                  Turn off
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        {!isEditing ? (
          <Button
            onClick={handleEdit}
            startIcon={<EditIcon />}
            variant="outlined"
          >
            Edit Profile
          </Button>
        ) : (
          <>
            <Button
              onClick={handleCancel}
              startIcon={<CancelIcon />}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              startIcon={<SaveIcon />}
              variant="contained"
            >
              Save Changes
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserProfileDialog; 